import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Label,
  FormGroup,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Alert,
  Progress,
  Input,
  Spinner
} from 'reactstrap';
import { connect } from 'react-redux';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageDataLoader from '../Utility/PageDataLoader';
import Paginator from '../Utility/Paginator';
import {
  getBatchImageList,
  publishBatchImage,
  addBatchImage,
  bulkPublishBatchImages
} from '../../api/batchImage';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { stringify as QStringify } from 'qs';
import BatchImageFilterModal from './BatchImageFilterModal';
import { getBatchList } from '../../api/batch';
import { validatePermissionKey } from '../../helpers/permission';
import {
  DELETE_BATCH_IMAGE,
  PUBLISH_BATCH_IMAGE,
  CREATE_BATCH_IMAGE
} from '../Utility/PermissionKeys';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FormUpload from '../../components/FormUpload';
import BulkDeleteImageModal from './BulkDeleteImageModal';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const BatchImageListPage = ({ authUser }) => {
  const history = useHistory();
  const [breadcrumbItems] = useState([
    { title: 'Batch Images', link: '#' },
    { title: 'View List', link: '#' }
  ]);
  const [batchImageList, setBatchImageList] = useState([]);
  const [batchImageLoading, setBatchImageLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageLimit: 10,
    totalEntries: 0,
    totalPages: 0
  });
  const [filterError, setFilterError] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentFilterCount, setCurrentFilterCount] = useState(0);
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null
  });
  const [exactDate, setExactDate] = useState(null);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatchOption, setSelectedBatchOption] = useState(null);
  const [sortingRule, setSortingRule] = useState('date$desc');
  const [isPublishedFilter, setIsPublishedFilter] = useState('');
  const [addImageModalOpen, setAddImageModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [images, setImages] = useState([]);
  const [addImageLoading, setAddImageLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [addImageError, setAddImageError] = useState({
    show: false,
    message: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkPublishing, setBulkPublishing] = useState(false);

  // Alerts state
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (authUser) {
      fetchBatchOptions();
      populateSearchOptions(history?.location?.search);
    }
  }, [authUser]);

  // Show success alert
  const showSuccessAlert = (message) => {
    setAlertMessage(message);
    setSuccessAlert(true);
  };

  // Show error alert
  const showErrorAlert = (message) => {
    setAlertMessage(message);
    setErrorAlert(true);
  };

  const handlePagination = (page) => {
    fetchData(page, pagination.pageLimit, createQueryOptions(), sortingRule);
  };

  const fetchData = async (
    current_page = 1,
    page_limit = 10,
    queryOptions = {},
    sortingOption = sortingRule
  ) => {
    setBatchImageLoading(true);
    try {
      const response = await getBatchImageList({
        ...queryOptions,
        page: current_page,
        limit: page_limit,
        sorting_option: sortingOption
      });

      if (response) {
        setPagination({
          currentPage: response.metadata.page,
          pageLimit: response.metadata.limit,
          totalPages: response.metadata.total_pages,
          totalEntries: response.metadata.total_results
        });
        setBatchImageList(response.data);
        setBatchImageLoading(false);
      }
    } catch (error) {
      showErrorAlert('Failed to load batch images');
      setBatchImageLoading(false);
    }
  };

  async function fetchBatchOptions() {
    try {
      const response = await getBatchList(1, 1000, {}, 'name$asc');
      if (response) {
        const options = response.data.map((batch) => ({
          value: batch._id,
          label: batch.name,
          startDate: new Date(batch.start_date),
          endDate: new Date(batch.end_date)
        }));
        setBatchOptions(options);
      }
    } catch (error) {
      showErrorAlert('Failed to load batch options');
    }
  }

  function createQueryOptions(
    exactdate = exactDate,
    batch = selectedBatchOption,
    publishedStatus = isPublishedFilter
  ) {
    const exactDateISOString = exactdate
      ? new Date(exactdate)
      : '';
    const selectedBatchId = batch ? batch.value : '';
    const queryOptions = {
      filter_exact_date: exactDateISOString,
      filter_batch: selectedBatchId,
      filter_published: publishedStatus,
    };
    updateUrlPath(queryOptions);
    updateFilterCount(queryOptions);
    return queryOptions;
  }

  function updateFilterCount(option) {
    let count = 0;
    Object.keys(option).forEach((key) => {
      if (option[key]?.toString()?.trim()?.length > 0) {
        count += 1;
      }
    });
    setCurrentFilterCount(count);
  }

  function populateSearchOptions(searchQuery) {
    const parsedQuery = new URLSearchParams(searchQuery.substring(1));
    const queryParams = {};
    for (let [key, value] of parsedQuery.entries()) {
      queryParams[key] = value;
    }
    if (queryParams.filter_start_date && queryParams.filter_end_date) {
      setDateFilter({
        startDate: new Date(queryParams.filter_start_date),
        endDate: new Date(queryParams.filter_end_date)
      });
    }
    if (queryParams.filter_exact_date) {
      setExactDate(new Date(queryParams.filter_exact_date));
    }
    if (queryParams.filter_batch) {
      const batchId = queryParams.filter_batch;
      const selected = batchOptions.find((option) => option.value === batchId);
      setSelectedBatchOption(selected || null);
    }
    setIsPublishedFilter(queryParams.filter_published || '');
    setSortingRule(queryParams.sorting_option || sortingRule);
    fetchData(1, pagination.pageLimit, queryParams, queryParams.sorting_option);
  }

  function updateUrlPath(queryOptions) {
    const queryString = QStringify(queryOptions);
    history.replace(`/batches/images?${queryString}`);
  }

  async function fetchSortedList(sortingOption) {
    setSortingRule(sortingOption);
    const queryOptions = createQueryOptions();
    await fetchData(1, null, queryOptions, sortingOption);
  }

  function openFilterModal() {
    setFilterModalOpen(true);
  }

  function closeFilterModal() {
    setFilterModalOpen(false);
  }

  async function handleTogglePublish(batchId, date) {
    try {
      await publishBatchImage(batchId, date);
      showSuccessAlert('Publish status updated successfully');
      fetchData(pagination.currentPage, null, createQueryOptions());
    } catch (error) {
      showErrorAlert('Failed to update publish status');
    }
  }

  function openAddImageModal() {
    setAddImageModalOpen(true);
  }

  function closeAddImageModal() {
    setAddImageModalOpen(false);
    resetAddImageState();
  }

  function resetAddImageState() {
    setSelectedBatch(null);
    setSelectedDate(null);
    setImages([]);
    setProgress(0);
    setAddImageError({ show: false, message: '' });
  }

  async function handleAddImages() {
    if (!selectedBatch) {
      setAddImageError({ show: true, message: 'Please select a batch' });
      return;
    }
    if (!selectedDate) {
      setAddImageError({ show: true, message: 'Please select a date' });
      return;
    }
    if (images.length === 0) {
      setAddImageError({
        show: true,
        message: 'Please add at least one image'
      });
      return;
    }
    const batch = batchOptions.find((b) => b.value === selectedBatch.value);
    const selectedDateObj = new Date(selectedDate);

    if (selectedDateObj < batch.startDate || selectedDateObj > batch.endDate) {
      setAddImageError({
        show: true,
        message: 'Selected date must be within the batch start and end dates'
      });
      return;
    }
    setAddImageLoading(true);
    setAddImageError({ show: false, message: '' });
    try {
      const formData = new FormData();
      formData.append('batch', selectedBatch.value);
      formData.append('date', moment(selectedDate).format('YYYY-MM-DD'));

      images.forEach((image, index) => {
        formData.append(`images`, image);
      });

      await addBatchImage(formData);

      showSuccessAlert('Images added successfully');
      closeAddImageModal();
      fetchData(pagination.currentPage, null, createQueryOptions());
    } catch (error) {
      showErrorAlert('Failed to add images');
      setAddImageError({
        show: true,
        message: error.message || 'Failed to add images'
      });
    } finally {
      setAddImageLoading(false);
    }
  }

  function closeBulkDeleteModal() {
    setSelectedDate(null);
    setBulkDeleteModalOpen(false);
  }

  const handleBulkDelete = async (batchId, date, batchImages) => {
    setSelectedBatchOption(batchId);
    setSelectedDate(moment(date).format('YYYY-MM-DD'));
    const imageIds = batchImages.map((image) => image._id);
    setSelectedImages(imageIds);
    setBulkDeleteModalOpen(true);
  };

  const applyFilters = async () => {
    const queryOptions = createQueryOptions();
    closeFilterModal();
    await fetchData(1, pagination.pageLimit, queryOptions, sortingRule);
  };

  const resetFilters = async () => {
    setDateFilter({ startDate: null, endDate: null });
    setSelectedBatchOption(null);
    setExactDate(null);
    setIsPublishedFilter('');
    closeFilterModal();
    const queryOptions = createQueryOptions(null, null, null);
    await fetchData(1, pagination.pageLimit, queryOptions, sortingRule);
  };

  const selectAllRows = () => {
    if (selectedRows.length === batchImageList.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(
        batchImageList.map((batch) => ({
          batchId: batch?.batch?._id,
          date: batch?.date,
          batchName: batch?.batch?.name
        }))
      );
    }
  };

  const isAllSelected = () => {
    return (
      selectedRows.length > 0 && selectedRows.length === batchImageList.length
    );
  };

  const isRowSelected = (batch) => {
    return selectedRows.some(
      (item) =>
        item?.batchId === batch?.batch?._id && item?.date === batch?.date
    );
  };

  const toggleRowSelection = (batch) => {
    setSelectedRows((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          item?.batchId === batch?.batch?._id && item?.date === batch?.date
      );
      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [
          ...prev,
          {
            batchId: batch?.batch?._id,
            date: batch?.date,
            batchName: batch?.batch?.name
          }
        ];
      }
    });
  };

  const handleBulkPublish = async () => {
    if (selectedRows.length === 0) return;
    try {
      setBulkPublishing(true);
      const toPublish = selectedRows
        ?.filter((row) => {
          const batchData = batchImageList.find(
            (b) => b?.batch?._id === row.batchId && b.date === row.date
          );
          return batchData && batchData.draft;
        })
        ?.map((row) => ({ batchId: row.batchId, date: row.date }));

      if (toPublish.length === 0) {
        return showErrorAlert(
          `No Unpublished batches selected! Please selact atleast 1 unpublished batch`
        );
      }

      const data = { batches: toPublish };
      await bulkPublishBatchImages(data);
      setSuccessAlert(true);
      setAlertMessage('Batch Images Published Successfully');
      setSelectedRows([]);
      fetchData(
        pagination.currentPage,
        pagination.pageLimit,
        createQueryOptions(),
        sortingRule
      );
    } catch (error) {
      console.error('Failed to bulk publish batch images: ', error);
      showErrorAlert(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to publish batch images!'
      );
    } finally {
      setBulkPublishing(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        {/* Success Alert */}
        <SweetAlert
          success
          title="Success!"
          show={!!successAlert}
          onConfirm={() => setSuccessAlert(false)}
        >
          {alertMessage}
        </SweetAlert>

        {/* Error Alert */}
        <SweetAlert
          error
          title="Error!"
          show={errorAlert}
          onConfirm={() => setErrorAlert(false)}
        >
          {alertMessage}
        </SweetAlert>

        {/* Add Image Modal */}
        <Modal isOpen={addImageModalOpen} backdrop="static" size="lg">
          <ModalHeader toggle={closeAddImageModal}>
            Add Batch Images
          </ModalHeader>
          <ModalBody>
            {addImageError.show && (
              <Alert color="danger" className="mt-2">
                {addImageError.message}
              </Alert>
            )}

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Select Batch</Label>
                  <Select
                    options={batchOptions}
                    value={selectedBatch}
                    onChange={setSelectedBatch}
                    placeholder="Select a batch..."
                    isDisabled={addImageLoading}
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Select Date</Label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    className="form-control"
                    minDate={selectedBatch?.startDate || new Date()}
                    maxDate={selectedBatch?.endDate || new Date()}
                    placeholderText="Select a date"
                    disabled={!selectedBatch || addImageLoading}
                  />
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>Upload Images</Label>
                  <FormUpload
                    title="Batch Images"
                    setImages={setImages}
                    disabled={addImageLoading}
                  />
                </FormGroup>
              </Col>
            </Row>

            {addImageLoading && (
              <div className="mt-3">
                <Progress value={progress} />
                <div className="text-center mt-2">
                  Uploading {progress}% complete
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={closeAddImageModal}
              disabled={addImageLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAddImages}
              disabled={addImageLoading}
            >
              {addImageLoading ? 'Uploading...' : 'Add Images'}
            </Button>
          </ModalFooter>
        </Modal>

        <BulkDeleteImageModal
          modalOpenState={bulkDeleteModalOpen}
          closeModalHandler={closeBulkDeleteModal}
          batch={selectedBatchOption}
          date={selectedDate}
          imageIds={selectedImages}
          fetchNewData={() =>
            fetchData(
              pagination.currentPage,
              pagination.pageLimit,
              createQueryOptions()
            )
          }
          onSuccess={() => showSuccessAlert('Images deleted successfully')}
          onError={() => showErrorAlert('Failed to delete images')}
        />

        <BatchImageFilterModal
          filterError={filterError}
          modalOpenState={filterModalOpen}
          closeModalHandler={closeFilterModal}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          batchOptions={batchOptions}
          selectedBatchOption={selectedBatchOption}
          setBatchOption={setSelectedBatchOption}
          isPublished={isPublishedFilter}
          setIsPublished={setIsPublishedFilter}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          exactDate={exactDate}
          setExactDate={setExactDate}
        />

        <Container fluid>
          <Breadcrumbs title="Batch Images" breadcrumbItems={breadcrumbItems} />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <Row className="mt-2">
                    <Col md="2">
                      <FormGroup>
                        <Label>Show Entries</Label>
                        <select
                          className="form-control"
                          onChange={(e) => {
                            fetchData(1, e.target.value);
                            setPagination({
                              ...pagination,
                              pageLimit: e.target.value
                            });
                          }}
                          value={pagination.pageLimit}
                          disabled={batchImageLoading}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Sort Images</Label>
                        <select
                          className="form-control"
                          onChange={(e) => fetchSortedList(e.target.value)}
                          value={sortingRule}
                          disabled={batchImageLoading}
                        >
                          <option value="date$desc">Date (Newest First)</option>
                          <option value="date$asc">Date (Oldest First)</option>
                          <option value="updated_at$desc">
                            Updated At (Newest First)
                          </option>
                          <option value="updated_at$asc">
                            Updated At (Oldest First)
                          </option>
                        </select>
                      </FormGroup>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      {selectedRows.length > 0 &&
                        validatePermissionKey(
                          authUser,
                          PUBLISH_BATCH_IMAGE
                        ) && (
                          <Button
                            size="sm"
                            color="success"
                            className="mr-2"
                            onClick={handleBulkPublish}
                            disabled={bulkPublishing}
                          >
                            {bulkPublishing ? (
                              <>
                                <Spinner size="sm" className="mr-2" />
                                Publishing...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-upload mr-2"></i>
                                Publish Selected ({selectedRows.length})
                              </>
                            )}
                          </Button>
                        )}
                      <Button
                        size="sm"
                        color="primary"
                        className="mr-3"
                        onClick={openFilterModal}
                      >
                        <i className="ri-filter-fill align-middle mr-2"></i>
                        Filter Queries
                        {currentFilterCount > 0 && (
                          <Badge color="light" pill className="ml-2">
                            {currentFilterCount}
                          </Badge>
                        )}
                      </Button>
                      {validatePermissionKey(authUser, CREATE_BATCH_IMAGE) && (
                        <Button
                          size="sm"
                          color="success"
                          onClick={openAddImageModal}
                        >
                          <i className="ri-add-line align-middle mr-2"></i>
                          Add Image
                        </Button>
                      )}
                    </Col>
                  </Row>
                  {batchImageLoading ? (
                    <div className="text-center">
                      <PageDataLoader />
                    </div>
                  ) : (
                    <div className="table-rep-plugin">
                      <div
                        className="table-responsive mb-0"
                        data-pattern="priority-columns"
                      >
                        <Table striped bordered responsive>
                          <thead>
                            <tr>
                              {validatePermissionKey(
                                authUser,
                                PUBLISH_BATCH_IMAGE
                              ) && (
                                  <th>
                                    <FormGroup check className="mb-4">
                                      <Input
                                        type="checkbox"
                                        checked={isAllSelected()}
                                        onChange={selectAllRows}
                                      />
                                    </FormGroup>
                                  </th>
                                )}
                              <th>SL. No</th>
                              <th>Batch Name</th>
                              <th>Date</th>
                              <th>Images Count</th>
                              <th>Published Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batchImageList?.map((item, index) => {
                              const isSelected = isRowSelected(item);
                              return (
                                <tr key={index}>
                                  {validatePermissionKey(
                                    authUser,
                                    PUBLISH_BATCH_IMAGE
                                  ) && (
                                      <td>
                                        <FormGroup check>
                                          <Input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() =>
                                              toggleRowSelection(item)
                                            }
                                          />
                                        </FormGroup>
                                      </td>
                                    )}
                                  <td>{index + 1}</td>
                                  <td>
                                    <Link to={`/batches/view/${item.batch._id}`}>
                                      {item.batch?.name || '--'}
                                    </Link>
                                  </td>
                                  <td>
                                    {moment(item.date).format('DD MMM YYYY')}
                                  </td>
                                  <td>{item.images?.length || 0}</td>
                                  <td>
                                    <Badge
                                      style={{
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                      }}
                                      color={
                                        !item.draft ? 'success' : 'secondary'
                                      }
                                    >
                                      {!item.draft
                                        ? 'Published'
                                        : 'Unpublished'}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      color="primary"
                                      className=" waves-effect waves-light mr-2"
                                      onClick={() => {
                                        history.push({
                                          pathname: `/batches/view/${item.batch._id}`,
                                          state: {
                                            activeTab: 'batch_image',
                                            date: moment(item.date).format(
                                              'YYYY-MM-DD'
                                            )
                                          }
                                        });
                                      }}
                                    >
                                      <i className="fas fa-eye mr-2"></i>
                                      View
                                    </Button>
                                    {validatePermissionKey(
                                      authUser,
                                      PUBLISH_BATCH_IMAGE
                                    ) && (
                                        <Button
                                          size="sm"
                                          color={
                                            !item.draft ? 'secondary' : 'success'
                                          }
                                          className="mr-2"
                                          onClick={() =>
                                            handleTogglePublish(
                                              item.batch._id,
                                              item.date
                                            )
                                          }
                                          disabled={
                                            item.draft === false ||
                                            item?.draft == undefined ||
                                            item?.draft == null
                                          }
                                        >
                                          <i className="fas fa-upload mr-2"></i>
                                          Publish
                                        </Button>
                                      )}
                                    {validatePermissionKey(
                                      authUser,
                                      DELETE_BATCH_IMAGE
                                    ) && (
                                        <Button
                                          size="sm"
                                          color="danger"
                                          className=" waves-effect waves-light"
                                          onClick={() => {
                                            handleBulkDelete(
                                              item.batch._id,
                                              item.date,
                                              item.images
                                            );
                                          }}
                                        >
                                          <i className="fas fa-trash mr-2"></i>
                                          Delete
                                        </Button>
                                      )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                  <Row className="mt-2">
                    <Col md="5">
                      <p>
                        <strong>
                          Showing{' '}
                          {(pagination.currentPage - 1) * pagination.pageLimit +
                            1}{' '}
                          to{' '}
                          {Math.min(
                            pagination.currentPage * pagination.pageLimit,
                            pagination.totalEntries
                          )}{' '}
                          of {pagination.totalEntries} entries
                        </strong>
                      </p>
                    </Col>
                    {!batchImageLoading && batchImageList.length > 0 && (
                      <Col md="7" className="d-flex justify-content-end">
                        <Paginator
                          totalPages={pagination.totalPages}
                          currentPage={pagination.currentPage}
                          changePage={handlePagination}
                        />
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Login.user
});

export default connect(mapStateToProps)(BatchImageListPage);